import { updateMerkzettelStateInMenu } from "@modules/bookmarks/bookmarks-menu-item.ts"
import { showEmptyBookmarksInfoBoxWhenNoEntries } from "@modules/bookmarks/entries/sideBarOptions.ts"
import type { TagData } from "@modules/bookmarks/tags/tags-api"

export type EntriesErrorResponse = {
    messageId: "string"
    message: "string"
    error: "string"
}

type EntriesSuccessResponse = {
    messageId: "string"
    message: "string"
    entries: JsonEntryData[]
    count: number
}

export type JsonEntryData = {
    advertId: number
    canonicalId: string
    profileId: number
    isOnline: boolean
    rating: number
    notes: string
    name: string
    locality: string
    thumbnailUrlPublic: string
    thumbnailUrlUncensored: string
    seoUrl: string
    tags: TagData[]
}

export const updateEntryAnnotations = async (
    userId: number,
    canonicalId: string,
    advertId: number,
    rating: number,
    notes: string,
): Promise<boolean> => {
    return fetch(`/api/bookmarks/${userId}/annotation/update`, {
        method: "POST",
        body: JSON.stringify({
            canonicalId,
            advertId,
            rating,
            notes,
        }),
    }).then(async (apiResponse: Response): Promise<boolean> => {
        if (!apiResponse.ok) {
            throw (await apiResponse.json()) as Promise<EntriesErrorResponse>
        }

        return !("error" in (await ((await apiResponse.json()) as Promise<EntriesSuccessResponse>)))
    })
}

export const fetchJsonEntriesFromApi = async (userId: number): Promise<JsonEntryData[]> => {
    return fetch(`/api/bookmarks/${userId}/list`).then(async (apiResponse: Response): Promise<JsonEntryData[]> => {
        if (!apiResponse.ok) {
            throw (await apiResponse.json()) as Promise<EntriesErrorResponse>
        }

        return (await ((await apiResponse.json()) as Promise<EntriesSuccessResponse>)).entries
    })
}

export const fetchJsonCookieEntriesFromApi = async (): Promise<JsonEntryData[]> => {
    return fetch("/api/bookmarks/list").then(async (apiResponse: Response): Promise<JsonEntryData[]> => {
        if (!apiResponse.ok) {
            throw (await apiResponse.json()) as Promise<EntriesErrorResponse>
        }

        return (await ((await apiResponse.json()) as Promise<EntriesSuccessResponse>)).entries
    })
}

export const fetchCountFromApi = async (userId: number): Promise<number> => {
    return fetch(`/api/bookmarks/${userId}/list?getCountOnly=true`).then(
        async (apiResponse: Response): Promise<number> => {
            if (!apiResponse.ok) {
                throw (await apiResponse.json()) as Promise<EntriesErrorResponse>
            }

            return (await ((await apiResponse.json()) as Promise<EntriesSuccessResponse>)).count
        },
    )
}

export const fetchCookieCountFromApi = async (): Promise<number> => {
    return fetch("/api/bookmarks/list?getCountOnly=true").then(async (apiResponse: Response): Promise<number> => {
        if (!apiResponse.ok) {
            throw (await apiResponse.json()) as Promise<EntriesErrorResponse>
        }

        return (await ((await apiResponse.json()) as Promise<EntriesSuccessResponse>)).count
    })
}

export const deleteCookieEntry = async (
    canonicalId: string,
    advertId: number,
    cardElement?: Element,
): Promise<boolean> => {
    return fetch("/api/bookmarks/delete", {
        method: "POST",
        body: JSON.stringify({ canonicalId, advertId, confirm: true }),
    }).then(async (apiResponse: Response): Promise<boolean> => {
        if (apiResponse.status === 304) {
            return false
        }

        if (!apiResponse.ok) {
            throw (await apiResponse.json()) as Promise<EntriesErrorResponse>
        }

        cardElement?.closest("li")?.remove()

        showEmptyBookmarksInfoBoxWhenNoEntries()

        updateMerkzettelStateInMenu(true)

        return !("error" in (await ((await apiResponse.json()) as Promise<EntriesSuccessResponse>)))
    })
}

export const deleteEntry = async (
    userId: number,
    canonicalId: string,
    advertId: number,
    cardElement?: Element,
): Promise<boolean> => {
    return fetch(`/api/bookmarks/${userId}/delete`, {
        method: "POST",
        body: JSON.stringify({ canonicalId, advertId, confirm: true }),
    }).then(async (apiResponse: Response): Promise<boolean> => {
        if (apiResponse.status === 304) {
            return false
        }

        if (!apiResponse.ok) {
            throw (await apiResponse.json()) as Promise<EntriesErrorResponse>
        }

        cardElement?.closest("li")?.remove()

        showEmptyBookmarksInfoBoxWhenNoEntries()

        updateMerkzettelStateInMenu(true)

        return !("error" in (await ((await apiResponse.json()) as Promise<EntriesSuccessResponse>)))
    })
}
